import type { Theme } from "../types";

type LanguagePref = `auto` | `en` | `es` | `pt`;

interface UserPreferences {
  theme: Theme;
  language: LanguagePref;
}

const defaultPreferences: UserPreferences = {
  theme: `light`,
  language: `auto`,
};

export const getUserPreferences = (): UserPreferences => {
  const stored = localStorage.getItem(`userPreferences`);

  if (stored) {
    const parsed = JSON.parse(stored) as Partial<UserPreferences>;

    const result: UserPreferences = { ...defaultPreferences };

    if (parsed.theme === `light` || parsed.theme === `dark` || parsed.theme === `auto`) {
      result.theme = parsed.theme;
    }

    if (
      parsed.language === `en` ||
      parsed.language === `es` ||
      parsed.language === `pt` ||
      parsed.language === `auto`
    ) {
      result.language = parsed.language;
    }

    return result;
  }

  const oldTheme = localStorage.getItem(`theme`);
  const oldLanguage = localStorage.getItem(`languagePreference`);

  // First run (no stored preferences). Choose a concrete theme based on OS preference
  const prefersDark = window.matchMedia(`(prefers-color-scheme: dark)`).matches;

  const migrated: UserPreferences = { ...defaultPreferences, theme: prefersDark ? `dark` : `light` };

  if (oldTheme === `light` || oldTheme === `dark` || oldTheme === `auto`) {
    // If legacy storage had 'auto', convert to a concrete choice using OS preference
    if (oldTheme === `auto`) {
      if (prefersDark) {
        migrated.theme = `dark` as Theme;
      } else {
        migrated.theme = `light` as Theme;
      }
    } else {
      migrated.theme = oldTheme as Theme;
    }
  }

  if (oldLanguage === `en` || oldLanguage === `es` || oldLanguage === `pt` || oldLanguage === `auto`) {
    migrated.language = oldLanguage as LanguagePref;
  }

  localStorage.setItem(`userPreferences`, JSON.stringify(migrated));
  localStorage.removeItem(`theme`);
  localStorage.removeItem(`languagePreference`);

  return migrated;
};

export const setUserPreferences = (prefs: Partial<UserPreferences>): UserPreferences => {
  const current = getUserPreferences();

  const updated: UserPreferences = { ...current, ...prefs };

  localStorage.setItem(`userPreferences`, JSON.stringify(updated));
  return updated;
};
